import PageMain from '@/components/page-main';
import { useEffect, useState, useReducer, useMemo } from 'react';
import {
  Popconfirm,
  message,
  Space,
  Input,
  Form,
  Select,
  Switch,
  Button,
  Table,
  Tooltip,
  Tag,
} from 'antd';
import useFetch from '@/hooks/useFetch';
import fetchValueReducer from '@/hooks/fetchValueReducer';
import {
  PgTagItem,
  QAItem,
  QAUpdateProp,
  TagComponent,
  TagEditComponent,
} from './components/productQA-item';
import { request } from '@/utils/request';
import QAHistory, { VersionChangeProps } from './components/QA-history';
import EditorSetter from './components/editor-setter';
import { KnowledgeProduct, Staff } from '@/models/user';
import ImportQuestion, { InsertItem } from './components/import-question';
import MultifunctionalModule from './components/multifunctional-module';
import FindSimilarity, {
  FindSimilarityCurrentPQ,
} from './components/find-similarity';
import pubSub from './pubSub';
import MakeTag from './components/make-tag';
import { Tag as TagProps } from './tag';
import type { ColumnsType } from 'antd/es/table';
import { useAccess, Access } from 'umi';
import {
  DeleteOutlined,
  VerifiedOutlined,
  QuestionCircleOutlined,
  HistoryOutlined,
  RocketOutlined,
  RetweetOutlined,
  ArrowDownOutlined,
} from '@ant-design/icons';

import styles from './index.less';
import useBreathe from './hooks/useBreathe';
import useResultModal from './hooks/useResultModal';
import useVerified, { VerifyItem } from './hooks/useVerified';
import { VERIFIED_STATUS } from '@bb8/config';
import useKnowledgeProduct from './hooks/useKnowledgeProduct';
import EditComponent from './components/EditComponent/index';
import QATool from './components/QA-tool';
import useReplaceModal, { ReplaceDataType } from './hooks/useReplaceModal';
import usePrompt from './hooks/usePrompt';

export const getQAtagNameById = (t: string[] | null, tags: TagProps[]) => {
  if (!t) return [];
  return t.map((vo) => {
    const item = tags.find((item: TagProps) => String(item.id) === vo);
    return item ? item.concatName : vo;
  });
};

export type PredictionData = {
  rowId: number;
  question: string;
  answer: string;
  product: string;
}[];

const Index: React.FC = () => {
  const [form] = Form.useForm();
  const [columnSearchValue, setColumnSearchValue] = useState<{
    question: string;
    answer: string;
    remark: string;
    trigger: string;
  }>({ question: '', answer: '', remark: '', trigger: '' });
  const [judgementMode, setJudgementMode] = useState<boolean>(false);
  const [fetchValue, dispatchFetchValue] = useReducer(fetchValueReducer, {
    page: 1,
    size: 10,
    total: 1,
  });
  const {
    data: baseQAList,
    loading,
    fetchData,
  } = useFetch(
    '/local/knowledge/productQA',
    { ...fetchValue, ...columnSearchValue, ...form.getFieldsValue(), judgementMode },
    dispatchFetchValue,
  );
  const { data: tags } = useFetch('/local/knowledge/tag', { isLeaf: 1 });
  const { data: users } = useFetch('/local/knowledge/productQA/worker', {});
  const [checkedList, setCheckedList] = useState<number[]>([]);
  const [expandedRowKeys, setExpandedRowKeys] = useState<number[]>([]);
  const [hideColumn, setHideColumn] = useState<boolean>(true);

  const { data: productNames } = useKnowledgeProduct();

  const [findSimilarityCurrentPQ, setFindSimilarityCurrentPQ] =
    useState<FindSimilarityCurrentPQ>({ id: 0, productName: '' });
  const [operatingRow, setOperatingRow] = useState<number[]>([]);
  const [hoverRow, setHoverRow] = useState<number[]>([]);

  const { E: ResultModal, setController: setCallbackResult } = useResultModal();
  const { E: VerifiedModal, controller: verifiedController } =
    useVerified(handleVerify);
  const { E: ReplaceModal, controller: replaceModalController } =
    useReplaceModal(handleReplaceKeyword);

  const [pqTagItem, setPgTagItem] = useState<PgTagItem>({
    from: 0,
    currentTags: [],
  });
  const access = useAccess();
  const { breathing, addBreathing } = useBreathe();
  const { E: PromptModal, controller: PromptController } = usePrompt();

  useEffect(() => {
    pubSub.setListCallback(fetchData);
    return () => {
      pubSub.clear();
    };
  }, []);

  useEffect(() => {
    fetchData();
  }, [columnSearchValue, judgementMode]);

  const activeRow = useMemo(() => {
    return Array.from(new Set([...hoverRow, ...operatingRow]));
  }, [hoverRow, operatingRow]);

  const list: QAItem[] = useMemo(() => {
    return (baseQAList as QAItem[]).map((vo, index) => {
      if (index === 0) {
        return {
          ...vo,
          isFirstOfItsKind: true,
        }
      } else if (JSON.stringify(vo.tags) !== JSON.stringify(baseQAList[index - 1].tags)) {
        return {
          ...vo,
          isFirstOfItsKind: true,
        }
      }
      return vo;
    });
  }, [baseQAList]);

  async function handleReplaceKeyword(data: ReplaceDataType, text: string) {
    const replaceList = data.ids
      .map((id) => {
        const qa = list.find((item: QAItem) => item.id === id);

        if (!qa || !qa.permit || qa.verifiedStatus === 'STORED') return null;

        return {
          id,
          value: (qa as any)[data.prop].replace(
            new RegExp((columnSearchValue as any)[data.prop], 'g'),
            text,
          ),
        };
      })
      .filter((vo) => Boolean(vo));

    if (replaceList.length === 0) {
      message.error('无权限进行替换！');
      return false;
    }

    const { resp } = await request(`/local/knowledge/productQA/replace`, {
      method: 'POST',
      data: {
        data: replaceList,
        prop: data.prop,
      },
    });
    if (resp?.code !== 0) return message.error(resp?.message);
    message.success(resp?.message);
    setCheckedList([]);
    fetchData(resp.data);
    addBreathing(resp?.data.value);
    return true;
  }

  const handleDelete = async (id: number) => {
    const { resp } = await request(`/local/knowledge/productQA/${id}`, {
      method: 'DELETE',
    });
    if (resp?.code !== 0) return message.error(resp?.message);
    message.success(resp?.message);
    fetchData();
  };

  const handleDeleteMany = async () => {
    const { resp } = await request(`/local/knowledge/productQA/deleteMany`, {
      method: 'POST',
      data: { ids: checkedList },
    });
    if (resp?.code !== 0) return message.error(resp?.message);
    message.success(resp?.message);
    fetchData();
  };

  const handleSave = async (payload: QAUpdateProp) => {
    const { resp } = await request(`/local/knowledge/productQA`, {
      method: 'POST',
      data: payload,
    });
    if (resp?.code !== 0) return message.error(resp?.message);
    message.success(resp?.message);
    fetchData(resp?.data);
    addBreathing(resp?.data.value);
  };

  const handleVersion = async (payload: VersionChangeProps) => {
    const { resp } = await request(`/local/knowledge/productQA/version`, {
      method: 'POST',
      data: payload,
    });
    if (resp?.code !== 0) return message.error(resp?.message);
    message.success(resp?.message);
    fetchData(resp?.data);
    addBreathing(resp?.data.value);
  };

  async function handleVerify(data: VerifyItem) {
    const { resp } = await request(`/local/knowledge/productQA/verify`, {
      method: 'POST',
      data,
    });
    if (resp?.code !== 0) {
      message.error(resp?.message);
      return false;
    }
    message.success(resp?.message);
    fetchData(resp?.data);
    addBreathing(resp?.data.value);
    return true;
  }

  const handleSetEditor = async (staff: Staff | undefined) => {
    if (!staff) return false;
    const { resp } = await request('/local/knowledge/productQA/editor', {
      method: 'POST',
      data: {
        ids: checkedList,
        ...staff,
      },
    });
    if (resp?.code !== 0) {
      message.error(resp?.message);
      return false;
    }
    message.success(resp.message);
    fetchData();
    return true;
  };

  const handleInsertData = async (data: InsertItem[]) => {
    const { resp, error } = await request('/local/knowledge/productQA/insert', {
      method: 'POST',
      data: { data },
    });
    // const resp = { code: 0, message: "ok", data: ["586fde46-b8a7-4994-8192-231986f4045f", "8fd6a9f2-6248-4105-9159-bb97ab578de5"] };
    if (resp?.code !== 0) {
      message.error(error?.message);
      return false;
    }
    message.success(resp.message);
    resp.data.forEach((jobId: string) => pubSub.subscribeJob(jobId, () => { }));
    return true;
  };

  /** @description 批量预测或预复核 若是没有操作权限，则自定从列表中剔除 */
  const handleBatchPrediction = async (
    type: 'predict' | 'verify' = 'predict',
  ) => {
    predictiveOutput(
      list
        .filter((l: QAItem) => checkedList.includes(l.id) && l.permit)
        .map((vo: QAItem) => ({
          rowId: vo.id,
          question: vo.question,
          answer: vo.question,
          product: vo.productName,
        })),
      type,
    );
  };

  /** @description 预测或预复核 */
  const predictiveOutput = async (
    data: PredictionData,
    type: 'predict' | 'verify',
  ) => {
    const { resp } = await request('/local/knowledge/predict', {
      method: 'POST',
      data: {
        name: `产品问答-${type === 'predict' ? '预测输出' : '机器人预复核'}`,
        data,
        type,
      },
    });
    // const resp = { code: 0, data: "e42ca844-775a-43c9-819b-18b0db477196", message: "预测输出提交成功，需稍作等待！" }
    // const resp = { code: 0, data: "1", message: "预测输出提交成功，需稍作等待！" }

    if (resp?.code !== 0) return message.error(resp?.message);
    pubSub.subscribeJob(resp.data, () => { });
    message.success(resp?.message);
  };

  const handleSubmit = async (ids: number[]) => {
    const { resp } = await request(`/local/knowledge/productQA/submit`, {
      method: 'POST',
      data: { ids },
    });
    if (resp?.code !== 0) return message.error(resp?.message);
    message.success(resp?.message);
    fetchData(resp?.data);
    addBreathing(resp?.data.value);
  };

  const handleJudgement = async (value: number, id: number) => {
    fetchData({ type: 'update', value: [{ from: id, to: { id, judgement: value } }] });
    const { resp, error } = await request(`/local/knowledge/productQA/judgement`, {
      method: 'POST',
      data: { id, value },
    });
    if (resp?.code !== 0) return message.error(error?.message);
  }

  const handleExpandRowKeys = (key: number) => {
    if (expandedRowKeys.includes(key)) {
      setExpandedRowKeys(expandedRowKeys.filter((k) => k !== key));
    } else {
      setExpandedRowKeys([...expandedRowKeys, key]);
    }
  };

  const getColumnSearchProps = (dataIndex: string) => (
    <Space>
      <Input
        size="small"
        placeholder="查询"
        allowClear
        style={{ width: 100 }}
        onChange={(e) =>
          setColumnSearchValue((pre) => ({
            ...pre,
            [dataIndex]: e.target.value,
          }))
        }
      />
      {dataIndex !== 'question' &&
        checkedList.length > 0 &&
        (columnSearchValue as any)[dataIndex] && (
          <Tooltip title="替换该关键字">
            <a
              onClick={() =>
                replaceModalController.setData({
                  ids: checkedList,
                  prop: dataIndex,
                })
              }
            >
              <RetweetOutlined />
            </a>
          </Tooltip>
        )}
    </Space>
  );

  const getPermissionAllowedQA = (ids: number[]) => {
    const arr = list.filter((item: QAItem) => ids.includes(item.id) && item.permit && item.verifiedStatus !== "STORED");
    if (arr.length === 0) {
      message.warning("没有可操作的问答！")
      return [];
    }
    return arr;
  }

  const columns: ColumnsType<QAItem> = [
    {
      title: '关键信息',
      dataIndex: 'other',
      render: (_, QA) => {
        const item = VERIFIED_STATUS.find(
          (vo: any) => vo.value === QA.verifiedStatus,
        ) as any;
        return (
          <Space direction="vertical" style={{ maxWidth: 160 }}>
            <Tooltip title="历史版本">
              <a onClick={() => handleExpandRowKeys(QA.id)}>
                <Tag color="orange">
                  <Space size={4}>
                    <HistoryOutlined />
                    {'v' + QA.version}
                  </Space>
                </Tag>
              </a>
            </Tooltip>
            {QA.verifiedComment &&
              QA.verifiedStatus === 'PENDING_MODIFICATION' ? (
              <Tooltip title={QA.verifiedComment}>
                <Tag color={item.color}>
                  <Space size={2}>
                    {item.label}
                    <QuestionCircleOutlined />
                  </Space>
                </Tag>
              </Tooltip>
            ) : (
              <Tag color={item.color}>{item.label}</Tag>
            )}
          </Space>
        );
      },
    },
    {
      title: '版本',
      dataIndex: 'version',
      render: (_, QA) => (
        <Tooltip title="历史版本">
          <a onClick={() => handleExpandRowKeys(QA.id)}>
            <Tag color="orange">
              <Space size={4}>
                <HistoryOutlined />
                {'v' + QA.version}
              </Space>
            </Tag>
          </a>
        </Tooltip>
      ),
    },
    {
      title: '产品名',
      dataIndex: 'productName',
      render: (_, QA) => (
        <TagComponent value={QA.productZhName} color="geekblue" />
      ),
    },
    {
      title: '状态',
      dataIndex: 'verifiedStatus',
      render: (_, record) => {
        const item = VERIFIED_STATUS.find((vo: any) => vo.value === _) as any;
        return _ === record.verifiedComment &&
          record.verifiedStatus === 'PENDING_MODIFICATION' ? (
          <Tooltip title={record.verifiedComment}>
            <Tag color={item.color}>
              <Space size={2}>
                {item.label}
                <QuestionCircleOutlined />
              </Space>
            </Tag>
          </Tooltip>
        ) : (
          <Tag color={item.color}>{item.label}</Tag>
        );
      },
    },
    ...[
      { prop: 'question', label: '问题', width: 'auto' },
      { prop: 'answer', label: '答案', width: '400px' },
      { prop: 'thinking', label: '思考过程', width: '300px' },
      { prop: 'prompt', label: '提示词', width: '300px' },
      { prop: 'trigger', label: '触发器', width: 'auto' },
      { prop: 'remark', label: '备注', width: 'auto' },
    ].filter(vo => !(judgementMode ? ['trigger'] : ['prompt']).includes(vo.prop)).map((vo) => ({
      title: (
        ['thinking', 'prompt'].includes(vo.prop) ? vo.label : <Space wrap>
          {vo.label} {getColumnSearchProps(vo.prop)}
        </Space>
      ),
      dataIndex: vo.prop,
      width: vo.width,
      render: (_: string, record: QAItem) => (<EditComponent
        QA={record}
        item={vo}
        autoSize={activeRow.includes(record.id)}
        highlightText={(columnSearchValue as any)[vo.prop]}
        isActive={activeRow.includes(record.id)}
        handleJudgement={handleJudgement}
      />
      ),
      onCell: (record: any) => {
        return {
          onMouseEnter: () => {
            setHoverRow([record.id]);
          },
          onMouseLeave: () => {
            setHoverRow(hoverRow.filter((item) => item !== record.id));
          },
        };
      },
    })),
    {
      title: '工具',
      render: (QA) => (
        <QATool
          QA={QA}
          validatePredict={
            Boolean(
              productNames.find(
                (vo: KnowledgeProduct) => vo.productName === QA.productName,
              )?.existPrompt,
            ) && QA.permit
          }
          handleSave={handleSave}
          setFindSimilarityCurrentPQ={setFindSimilarityCurrentPQ}
          predictiveOutput={predictiveOutput}
          setCallbackResult={setCallbackResult}
          handlePrompt={(ids: number[]) => PromptController.setQaList(getPermissionAllowedQA(ids))}
        />
      ),
    },
    {
      title: (
        <Tooltip
          color="#e6f7ff"
          title={
            <Space>
              <Tag color="gold">复核者</Tag>
              <Tag color="blue">创建者</Tag>
              <Tag color="green">协作者</Tag>
            </Space>
          }
        >
          <Space size={4}>
            操作成员
            <QuestionCircleOutlined />
          </Space>
        </Tooltip>
      ),
      dataIndex: 'creator',
      render: (c, _: QAItem) => {
        if (!_.verifiedName && !_.creator && !_.teamWorker) return <>-</>;
        return (
          <Space direction="vertical">
            <TagComponent value={_.verifiedName ?? ''} color="gold" />
            <TagComponent value={_?.creator?.name} color="blue" />
            <TagComponent value={_?.teamWorker?.name} color="green" />
          </Space>
        );
      },
    },
    {
      title: '标签',
      dataIndex: 'tags',
      render: (_, record) => (
        <TagEditComponent
          tags={getQAtagNameById(_, tags)}
          id={record.id}
          permit={record.permit}
          handleTag={(data: PgTagItem) =>
            setPgTagItem({ ...data, currentTags: _ || [] })
          }
        />
      ),
    },
    {
      title: '操作',
      render: (_, QA) => (
        <Space>
          <Access
            accessible={
              ['PENDING_SUBMISSION', 'PENDING_MODIFICATION'].includes(
                QA.verifiedStatus,
              ) &&
              access.productQAEdit &&
              QA.permit
            }
          >
            <Tooltip title="提交去复核">
              <Popconfirm
                title="您确定提交该条QA进行复核吗？"
                onConfirm={() => handleSubmit([QA.id])}
              >
                <a type="text" style={{ fontSize: 20 }}>
                  <RocketOutlined />
                </a>
              </Popconfirm>
            </Tooltip>
          </Access>
          <Access
            accessible={access.administrator && QA.verifiedStatus !== 'STORED'}
          >
            <Tooltip title="复核">
              <a
                style={{ fontSize: 20 }}
                onClick={() => verifiedController.setId([QA.id])}
              >
                <VerifiedOutlined />
              </a>
            </Tooltip>
          </Access>
          <Access
            accessible={QA.permit && access.productQADelete}
            fallback={<>-</>}
          >
            <Tooltip title="删除">
              <Popconfirm
                placement="topRight"
                title="您确定删除该条Q&A吗？"
                onConfirm={() => handleDelete(QA.id)}
              >
                <a style={{ fontSize: 20 }}>
                  <DeleteOutlined />
                </a>
              </Popconfirm>
            </Tooltip>
          </Access>
        </Space>
      ),
    },
  ];

  const rowSelection = {
    onChange: (selectedRowKeys: React.Key[], selectedRows: QAItem[]) => {
      setCheckedList(selectedRowKeys as number[]);
    },
    getCheckboxProps: (record: QAItem) => ({
      disabled: !record.setMaker || !access.productQAEdit, // Column configuration not to be checked
    }),
  };

  return (
    <PageMain>
      {/* <MultifunctionalModule /> */}
      <Space direction="vertical" style={{ width: '100%' }}>
        <Form
          form={form}
          initialValues={{
            question: '',
            relevance: false,
          }}
          layout="inline"
          onValuesChange={() => {
            dispatchFetchValue({ type: 'update', value: { page: 1 } });
            fetchData();
          }}
        >
          <Space style={{ flexWrap: 'wrap' }}>
            <Form.Item label="产品名" name="productName">
              <Select
                placeholder="请选择产品"
                allowClear
                style={{ width: 200 }}
              >
                {productNames.map((item) => (
                  <Select.Option
                    key={item.productName}
                    value={item.productName}
                  >
                    {item.productZhName}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              label="状态"
              name="verifiedStatus"
              tooltip={
                <ul>
                  {[
                    '待提交：已优化过的问答，提交复核后，变更为待复核状态；',
                    '待复核：已确定完成优化，可以进行复核；',
                    '已落库：复核通过后的问答状态；',
                    '待修改：需要优化的问答，也可直接提交复核；',
                  ].map((vo) => (
                    <li key={vo}>{vo}</li>
                  ))}
                </ul>
              }
            >
              <Select
                style={{ width: 200 }}
                placeholder="请选择当前问答状态"
                allowClear
              >
                {VERIFIED_STATUS.map((vo: any) => (
                  <Select.Option key={vo.value} value={vo.value}>
                    {vo.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item label="标签" name="tags">
              <Select
                mode="multiple"
                allowClear
                style={{ width: 300 }}
                dropdownMatchSelectWidth={false}
                placeholder="请选择标签"
                filterOption={(input, option) =>
                  (option?.key ?? '').includes(input)
                }
              >
                {tags.map((t: TagProps) => (
                  <Select.Option key={t.id + t.concatName} value={t.id}>
                    {t.concatName}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item label="操作成员" name="userId">
              <Select
                allowClear
                style={{ width: 200 }}
                placeholder="请选择操作成员"
              >
                {users.map((t: { id: number; name: string }) => (
                  <Select.Option key={t.id} value={t.id}>
                    {t.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Access accessible={!access.administrator}>
              <Form.Item
                label="只看自己相关问题"
                valuePropName="checked"
                name="relevance"
              >
                <Switch />
              </Form.Item>
            </Access>

            <Form.Item label="隐藏列">
              <Switch checked={hideColumn} onChange={(e) => setHideColumn(e)} />
            </Form.Item>

            <Form.Item label="错题本模式">
              <Switch checked={judgementMode} onChange={(e) => setJudgementMode(e)} />
            </Form.Item>
          </Space>
        </Form>
        <Space
          style={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}
        >
          {checkedList.length > 0 && (
            <>
              <EditorSetter handleSetEditor={handleSetEditor} />
              <Access accessible={access.administrator}>
                <Button type="primary" onClick={() => handleBatchPrediction()}>
                  预测输出
                </Button>
              </Access>
              <Access accessible={access.productQAEdit}>
                <Button
                  type="primary"
                  onClick={() => handleBatchPrediction('verify')}
                >
                  机器人预复核
                </Button>
              </Access>
              <Access accessible={access.productQAEdit}>
                <Button
                  type="primary"
                  onClick={() => PromptController.setQaList(getPermissionAllowedQA(checkedList))}
                >
                  生成回答
                </Button>
              </Access>
              <Access accessible={access.administrator}>
                <Button
                  type="primary"
                  onClick={async () => verifiedController.setId(checkedList)}
                >
                  复核
                </Button>
              </Access>
              <Access accessible={access.administrator}>
                <Popconfirm
                  title="是否确定批量删除？"
                  onConfirm={async () => {
                    await handleDeleteMany();
                  }}
                >
                  <Button type="primary">删除</Button>
                </Popconfirm>
              </Access>
            </>
          )}
          <ImportQuestion handleInsertData={handleInsertData} />
        </Space>
        <Table
          loading={loading}
          rowSelection={{
            type: 'checkbox',
            ...rowSelection,
            selectedRowKeys: checkedList,
          }}
          rowClassName={(e) => {
            return operatingRow.includes(e.id)
              ? styles.operatingRow
              : styles.hoverRow;
          }}
          onRow={(data) => {
            return {
              className: breathing.includes(data.id) ? styles.breathe : '',
              onClick: (e) => setOperatingRow([data.id]),
            };
          }}
          expandable={{
            expandedRowKeys,
            expandIcon: (data: any) => {
              const record = data.record;
              if (!record.isFirstOfItsKind) return <></>
              const result = getQAtagNameById(record.tags, tags);
              return <div className={styles.tagWrapper}>
                <Tag
                  style={{ fontSize: 15 }}
                  className={styles.tagItem}
                  color="#1890ff"
                  icon={<ArrowDownOutlined />}
                >
                  标签：{result && result.length > 0 ? result.join("｜") : "无"}
                </Tag>
              </div>
            },
            expandedRowRender: (record, index, indent, expanded) => (
              <QAHistory
                id={expanded ? record.id : 0}
                permit={record.permit}
                handleVersion={handleVersion}
              />
            ),
          }}
          rowKey={'id'}
          columns={columns.filter((vo: any) =>
            hideColumn
              ? ![
                'productName',
                'creator',
                'tags',
                'verifiedComment',
                'version',
                'verifiedStatus',
              ].includes(vo['dataIndex'] as any)
              : !['other'].includes(vo['dataIndex'] as any),
          )}
          dataSource={list}
          pagination={{
            showSizeChanger: true,
            current: fetchValue.page,
            total: fetchValue.total,
            pageSize: fetchValue.size,
            pageSizeOptions: [10, 30, 50, 100],
            onChange: (page, size) => {
              dispatchFetchValue({ type: 'update', value: { page, size } });
              fetchData();
              setCheckedList([]);
              setOperatingRow([]);
            },
            showTotal: (total) => `总计：${total}条`,
          }}
        />
      </Space>
      <FindSimilarity
        productNames={productNames}
        findSimilarityCurrentPQ={findSimilarityCurrentPQ}
        clearPQ={() => setFindSimilarityCurrentPQ({ id: 0, productName: '' })}
      />
      <MakeTag
        id={pqTagItem.from}
        currentTags={pqTagItem.currentTags}
        tags={tags}
        clearTag={() => setPgTagItem({ from: 0, currentTags: [] })}
        onResult={(data: any) => fetchData(data)}
      />
      {ResultModal}
      {VerifiedModal}
      {ReplaceModal}
      {PromptModal}
    </PageMain>
  );
};

export default Index;
